import { useEffect, useRef, useState, useCallback } from "react"

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""

let loadPromise: Promise<void> | null = null

function loadGoogleMaps(): Promise<void> {
  if (loadPromise) return loadPromise
  if (window.google?.maps) return Promise.resolve()

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Failed to load Google Maps"))
    document.head.appendChild(script)
  })

  return loadPromise
}

export function useGoogleMaps() {
  const [loaded, setLoaded] = useState(!!window.google?.maps)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (loaded) return
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((err) => setError(err.message))
  }, [loaded])

  return { loaded, error }
}

export function useAutocomplete(
  inputRef: React.RefObject<HTMLInputElement | null>,
  onPlaceSelected: (place: { address: string; formattedAddress: string; lat: number; lng: number }) => void
) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const { loaded } = useGoogleMaps()

  useEffect(() => {
    if (!loaded || !inputRef.current || autocompleteRef.current) return

    autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
    })

    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current?.getPlace()
      if (!place?.geometry?.location) return

      onPlaceSelected({
        address: inputRef.current?.value || "",
        formattedAddress: place.formatted_address || "",
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      })
    })

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current)
        autocompleteRef.current = null
      }
    }
  }, [loaded, inputRef, onPlaceSelected])
}

export function getSatelliteImageUrl(lat: number, lng: number, zoom = 20, size = "640x640"): string {
  return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${size}&maptype=satellite&key=${GOOGLE_MAPS_API_KEY}`
}

declare global {
  interface Window {
    google: any
  }
}
